// Pure freeze-geometry helpers for the shared viewer. Side-effect-free so
// they can be unit-tested under plain `node --test` without a DOM.
//
// computeSourceRect: given a <video> drawn with `object-fit: contain` and a
// CSS `transform: translate(tx,ty) scale(zoom)` (transform-origin = panel
// centre), return the rectangle of SOURCE pixels currently visible inside
// the panel. Used by the Freeze button so the JPEG is a native-pixel crop
// of what the reviewer sees, not the whole frame.

/**
 * @param {object} p
 * @param {number} p.videoWidth   Source frame width in pixels.
 * @param {number} p.videoHeight  Source frame height in pixels.
 * @param {number} p.panelWidth   Panel (.vmc-video-clip) width in CSS px.
 * @param {number} p.panelHeight  Panel height in CSS px.
 * @param {number} p.zoom         Zoom scale (> 0; in practice >= 1).
 * @param {number} p.tx           Pan X in CSS px (applied after scale).
 * @param {number} p.ty           Pan Y in CSS px.
 * @returns {{sx:number,sy:number,sw:number,sh:number}|null}
 *          Source-pixel crop rect, or null if degenerate.
 */
export function computeSourceRect(p) {
  const { videoWidth, videoHeight, panelWidth, panelHeight, zoom, tx, ty } = p;
  if (!(videoWidth > 0) || !(videoHeight > 0)) return null;
  if (!(panelWidth > 0) || !(panelHeight > 0)) return null;
  if (!(zoom > 0)) return null;

  // 1. object-fit: contain — content rect at zoom=1.
  const r = Math.min(panelWidth / videoWidth, panelHeight / videoHeight);
  const cw = videoWidth * r;
  const ch = videoHeight * r;

  // 2. Transformed content rect (scale about panel centre, then translate).
  const cw2 = cw * zoom;
  const ch2 = ch * zoom;
  const cx2 = (panelWidth - cw2) / 2 + tx;
  const cy2 = (panelHeight - ch2) / 2 + ty;

  // 3. Visible CSS rect = intersection with the panel.
  const vx = Math.max(cx2, 0);
  const vy = Math.max(cy2, 0);
  const vr = Math.min(cx2 + cw2, panelWidth);
  const vb = Math.min(cy2 + ch2, panelHeight);
  const vw = vr - vx;
  const vh = vb - vy;
  if (vw <= 0 || vh <= 0) return null;

  // 4. Inverse-map the visible CSS rect into source pixels.
  const scale = r * zoom;
  let sx = (vx - cx2) / scale;
  let sy = (vy - cy2) / scale;
  let sw = vw / scale;
  let sh = vh / scale;

  // 5. Clamp to the source frame. The intersection in step 3 already keeps
  // the inverse-mapped rect within bounds, so these are defensive guards
  // against float-rounding drift (a sub-pixel overshoot would otherwise
  // reach drawImage); the sub-pixel reject below is the only live exit.
  if (sx < 0) sx = 0;
  if (sy < 0) sy = 0;
  if (sx > videoWidth) sx = videoWidth;
  if (sy > videoHeight) sy = videoHeight;
  if (sw > videoWidth - sx) sw = videoWidth - sx;
  if (sh > videoHeight - sy) sh = videoHeight - sy;
  if (sw < 1 || sh < 1) return null;

  return { sx, sy, sw, sh };
}

/**
 * Target drawing-buffer size for a supersampled panorama capture. Scales the
 * current WebGL canvas size up by `factor` (default 2), but never lets the
 * long edge exceed `maxLongEdge` (default 4096). Aspect ratio is preserved.
 *
 * @param {number} w0  Current canvas backing-store width.
 * @param {number} h0  Current canvas backing-store height.
 * @param {{factor?:number,maxLongEdge?:number}} [opts]
 * @returns {{W:number,H:number}}
 */
export function computeSupersampleSize(w0, h0, opts = {}) {
  const factor = (Number.isFinite(opts.factor) && opts.factor > 0) ? opts.factor : 2;
  const maxLongEdge = (Number.isFinite(opts.maxLongEdge) && opts.maxLongEdge > 0)
    ? opts.maxLongEdge
    : 4096;
  const width = Math.max(1, Math.round(w0));
  const height = Math.max(1, Math.round(h0));
  const longEdge = Math.max(width, height);
  const k = Math.min(factor, maxLongEdge / longEdge);
  return {
    W: Math.max(1, Math.round(width * k)),
    H: Math.max(1, Math.round(height * k)),
  };
}
